import React, { useState } from 'react';
import _ from 'lodash';
import { Flex, Margin } from 'components/base/layout-components';
import Text from 'components/base/Text';
import SearchableListWithChips from 'components/SearchableListWithChips';
import InfoBox from 'components/InfoBox';
import CollapseSection from '../../components/CollapseSection';
import { ChipComponent, EmptyState } from './SearchListComponents';
import styled from 'styled-components';
import Icon from 'components/base/Icon';
import BrowserStorageAccessor from 'common/utils/BrowserStorageAccessor';
import { LOCAL_STORAGE_KEYS, TRACKING } from 'common/constants/constants';
import * as TrackingService from 'common/services/tracking.service';
import useToast from 'components/hooks/useToast';
import { getColor } from 'common/constants/_colors';

const StyledIcon = styled(Icon)`
  cursor: pointer;
  margin-left: 1.5rem;
  margin-right: 0.2rem;
`;

const TitleComponent = ({ recipients, showInfo, onCloseInfo }) => (
  <Flex weight='100%' items='center'>
    <Text
      letterSpacing='0.2px'
      size='large'
      weight={400}
      color='black'
      mr='4rem'
    >
      Mailing list:
    </Text>
    <Text size='normal' weight={400} color='shuttleGray' mr='2.5rem'>
      {`${recipients} recipients`}
    </Text>
    {showInfo && (
      <InfoBox mt={null} mb={null} padding='small'>
        <Flex items='center'>
          <Text size='medium' weight='400'>
            Please note that you can add any email to this list, not just
            LinearB users
          </Text>
          <StyledIcon
            name='roundedClose'
            size='smaller'
            color='shuttleGray'
            onClick={onCloseInfo}
          />
        </Flex>
      </InfoBox>
    )}
  </Flex>
);
const getResolvedSelected = ({ items, selectedItems }) => {
  const selectedEmails = selectedItems.map(({ email }) => email);
  const emailToItemsMap = _.keyBy(items, 'email');
  const emailToSelectedItemsMap = _.keyBy(selectedItems, 'email');
  const resolvedSelected = selectedEmails.map(email => ({
    ...emailToItemsMap[email],
    ...emailToSelectedItemsMap[email]
  }));
  const selectedWithoutUnsubscribed = resolvedSelected.filter(
    ({ unsubscribe }) => !unsubscribe
  );
  const unsubscribedSelected = resolvedSelected
    .filter(({ unsubscribe }) => unsubscribe)
    .map(item => ({
      ...item,
      chipProps: {
        style: { borderColor: getColor('radicalRed') },
        icon: 'RefreshArrows',
        iconSize: 'large',
        tooltipText: 'This person has unsubscribed. Click to re-subscribe.'
      }
    }));
  return [...selectedWithoutUnsubscribed, ...unsubscribedSelected];
};
const MailListSection = ({
  items,
  selectedItems,
  onAddItem,
  onRemoveItem,
  disabled
}) => {
  const localStorageAccessor = BrowserStorageAccessor().local;
  const toast = useToast();
  const [isInfoEnabled, setIsInfoEnabled] = useState(
    !localStorageAccessor.get(
      LOCAL_STORAGE_KEYS.HIDE_EMAIL_PREFERENCES_INFO_MESSAGE
    )
  );
  const onCloseInfo = () => {
    setIsInfoEnabled(false);
    localStorageAccessor.set(
      LOCAL_STORAGE_KEYS.HIDE_EMAIL_PREFERENCES_INFO_MESSAGE,
      true
    );
  };
  const onAddNewEmail = searchValue => () => {
    const itemAlreadySelected = selectedItems.some(
      ({ email }) => email === searchValue
    );
    if (itemAlreadySelected) {
      toast({
        variant: 'info',
        message: 'this email address is already on your mailing list'
      });
    } else {
      TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
        page: TRACKING.PAGES.SETTINGS,
        page_tab: TRACKING.PAGE_TABS.EMAIL_PREFERENCES,
        click_type: TRACKING.CLICK.ADD_TO_MAILING_LIST,
        email: searchValue
      });
      onAddItem({
        email: searchValue
      });
    }
  };
  return (
    <CollapseSection
      disabled={disabled}
      title={isOpen => (
        <TitleComponent
          recipients={selectedItems.length}
          showInfo={isOpen && isInfoEnabled}
          onCloseInfo={onCloseInfo}
        />
      )}
      openButtonText='View & edit'
      isTitleClickable={true}
    >
      <Margin t='normal'>
        <SearchableListWithChips
          chip={ChipComponent}
          placeholder='Add members & emails'
          chipsSearchPlaceholder='Search for recipients'
          searchKeys={['email', 'name']}
          items={items}
          onSelectItem={onAddItem}
          onRemoveItem={onRemoveItem}
          selectedItems={getResolvedSelected({ items, selectedItems })}
          emptyState={searchValue => (
            <EmptyState
              searchValue={searchValue}
              onAddClick={onAddNewEmail(searchValue)}
            />
          )}
        />
      </Margin>
    </CollapseSection>
  );
};
export default MailListSection;
